import React, { useCallback, useEffect, useState } from 'react';
import { ChevronDown, ChevronUp, ListChecks, ArrowRight, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { StatusTaskRow } from './StatusTaskRow';
import { LegacyBadge } from './LegacyBadge';

const pillCls = (s, sel) => {
  const base = s.state === 'complete' ? (s.unticked ? 'border-amber-300 text-amber-800 bg-amber-50' : 'border-emerald-300 text-emerald-800 bg-emerald-50')
    : s.state === 'in_progress' ? 'border-[#D35400] text-[#D35400] bg-orange-50' : 'border-[#E2E2DF] text-slate-400 bg-white';
  return `${base} ${sel ? 'ring-2 ring-offset-1 ring-[#0F4F1B]' : ''}`;
};

// Collapsible Appraisal Status Panel: every runner step per stage, ticked by the runner, CRM proof or by hand.
export const StatusPanel = ({ api, appraisalId, status, onMove }) => {
  const [open, setOpen] = useState(false);
  const [d, setD] = useState(null);
  const [sel, setSel] = useState('');
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/appraisals/${appraisalId}/status-panel`); setD(data); setSel((s) => s || data.current_key); }
    catch { /* panel is optional */ }
  }, [api, appraisalId]);
  useEffect(() => { setSel(''); load(); }, [load, status]);

  const tick = async (task, done) => {
    setBusy(task.id);
    try { const { data } = await api.post(`/appraisals/${appraisalId}/status-panel/tick`, { status_key: sel, task_id: task.id, done }); setD(data); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not save'); }
    finally { setBusy(''); }
  };

  if (!d) return null;
  const main = d.stages.filter((s) => !s.branch || s.key === d.current_key || s.state !== 'locked');
  const cur = d.stages.find((s) => s.key === d.current_key);
  const stage = d.stages.find((s) => s.key === sel) || cur || d.stages[0];
  const idx = main.findIndex((s) => s.key === d.current_key);
  const next = main.slice(idx + 1).find((s) => !s.branch);
  const doneCount = (s) => s.tasks.length - s.unticked;

  return (
    <div className="mt-3 rounded-lg border border-[#E2E2DF] bg-[#FAFAF8]" data-testid="status-panel">
      <div role="button" tabIndex={0} data-testid="status-panel-toggle" onClick={() => setOpen(!open)} onKeyDown={(e) => e.key === 'Enter' && setOpen(!open)} className="w-full flex flex-wrap items-center gap-2 px-4 py-2.5 text-left hover:bg-slate-50 rounded-lg cursor-pointer">
        <ListChecks size={15} className="text-[#0F4F1B]" />
        <span className="text-xs font-bold uppercase tracking-[0.18em] text-slate-600">Status panel</span>
        {cur ? <span className="text-xs text-slate-500" data-testid="status-panel-summary">{cur.label}: {doneCount(cur)}/{cur.tasks.length} steps OK'd</span>
          : <span className="text-xs text-slate-500" data-testid="status-panel-summary">Not on a workflow stage yet</span>}
        <LegacyBadge api={api} appraisalId={appraisalId} legacy={d.legacy} onChange={load} />
        <span className="ml-auto text-slate-400">{open ? <ChevronUp size={15} /> : <ChevronDown size={15} />}</span>
      </div>
      {open && stage && (
        <div className="px-4 pb-4 space-y-3" data-testid="status-panel-body">
          <div className="flex flex-wrap gap-1.5">
            {main.map((s) => (
              <button key={s.key} data-testid={`status-panel-stage-${s.key}`} onClick={() => setSel(s.key)} className={`text-[11px] px-2.5 py-1 rounded-md border transition-colors ${pillCls(s, s.key === stage.key)}`}>
                {s.label} <span className="font-mono opacity-70">{doneCount(s)}/{s.tasks.length}</span>
              </button>
            ))}
          </div>
          {stage.state === 'complete' && stage.unticked > 0 && (
            <div className="flex items-center gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2" data-testid="status-panel-unticked-warning">
              <AlertTriangle size={13} /> This stage is marked done but {stage.unticked} step(s) were never OK'd.
            </div>
          )}
          <ul className="bg-white border border-[#E2E2DF] rounded-md px-3" data-testid="status-panel-tasks">
            {stage.tasks.map((t) => <StatusTaskRow key={t.id} task={t} busy={busy === t.id} onTick={tick} canTick={stage.state !== 'locked' || stage.key === d.current_key} />)}
            {!stage.tasks.length && <li className="py-2 text-xs text-slate-400">No runner steps on this stage.</li>}
          </ul>
          {stage.task_mode === 'exclusive' && <p className="text-[11px] text-slate-400">Only one of these steps is needed.</p>}
          {stage.key === d.current_key && next && (
            <div className="flex items-center justify-end gap-3">
              {!stage.all_done && <span className="text-[11px] text-slate-500" data-testid="status-panel-not-ready">{stage.unticked} step(s) still to OK before moving on</span>}
              <button data-testid="status-panel-move-next-btn" onClick={() => onMove(next.value)}
                className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-md transition-colors ${stage.all_done ? 'bg-[#0F4F1B] text-white hover:bg-[#0a3c14]' : 'border border-[#E2E2DF] text-slate-500 hover:border-[#D35400] hover:text-[#D35400]'}`}>
                {stage.all_done ? 'All steps OK\'d — move to' : 'Move to'} {next.label} <ArrowRight size={12} />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
